import React, { useState } from 'react';
import { ArrowRight, ExternalLink, Filter, TrendingUp, Sparkles, CheckCircle2, ChevronDown, ChevronUp, Globe, Lock, Shield } from 'lucide-react';
import { CASE_STUDIES, CaseStudy } from '../data/siteData';
import { CustomImage } from './CustomImage';

interface CaseStudiesSectionProps {
  onSelectCaseStudy: (caseStudy: CaseStudy) => void;
  onOpenBooking: () => void;
  onViewAllWebsites?: () => void;
  isPageHeader?: boolean;
}

export const CaseStudiesSection: React.FC<CaseStudiesSectionProps> = ({
  onSelectCaseStudy,
  onOpenBooking,
  onViewAllWebsites,
  isPageHeader,
}) => {
  const [selectedFilter, setSelectedFilter] = useState<string>('All');
  const [showAllCases, setShowAllCases] = useState<boolean>(false);

  const categories = ['All', 'Solar & Clean Energy', 'Websites', 'Commerce', 'Corporate', 'Services'];

  const filteredStudies = CASE_STUDIES.filter((item) => {
    if (selectedFilter === 'All') return true;
    if (selectedFilter === 'Solar & Clean Energy') return item.id === 'shantis-designs' || item.category.includes('Solar');
    if (selectedFilter === 'Websites') return item.category.includes('Websites');
    if (selectedFilter === 'Commerce') return item.category.includes('Commerce') || item.category.includes('Hardware') || item.category.includes('Automotive');
    if (selectedFilter === 'Corporate') return item.category.includes('Corporate') || item.category.includes('Technology') || item.category.includes('Recruitment');
    if (selectedFilter === 'Services') return item.category.includes('Security') || item.category.includes('Wellness') || item.category.includes('Legal') || item.category.includes('Travel');
    return true;
  });

  // Homepage density control: show top 6 unless expanded
  const visibleStudies = showAllCases ? filteredStudies : filteredStudies.slice(0, 6);
  const remainingCount = filteredStudies.length - 6;

  return (
    <section id="case-studies" className="py-20 lg:py-28 bg-white border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl text-left">
            <div className="text-xs font-bold uppercase tracking-widest text-[#b88e4c] mb-2 font-mono">
              Selected Work · Production Deployments
            </div>
            {isPageHeader ? (
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Case studies &amp; live snippets.
              </h1>
            ) : (
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Case studies &amp; live snippets.
              </h2>
            )}
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
              Inspected snippets pulled directly from client domains across clean energy, ISP telecommunications, wholesale hardware, and corporate brands.
            </p>
          </div>

          {/* Interactive Filter Tabs */}
          <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100 rounded-xl self-start md:self-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedFilter(cat);
                  setShowAllCases(true); // expand on filter click
                }}
                className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                  selectedFilter === cat
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visibleStudies.map((study) => (
            <div
              key={study.id}
              onClick={() => onSelectCaseStudy(study)}
              className="flex flex-col justify-between rounded-3xl bg-white border border-slate-200 hover:border-slate-400 hover:shadow-xl transition-all duration-200 text-left overflow-hidden cursor-pointer group"
            >
              <div>
                {/* Real Browser Chrome Mockup with URL Bar */}
                <div className="bg-slate-950 px-4 py-3 border-b border-slate-800 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
                    <div className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
                    <div className="h-2.5 w-2.5 rounded-full bg-[#009fe3]/80" />
                  </div>

                  {/* Browser URL snippet */}
                  <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 text-slate-300 font-mono text-xs px-3 py-1 rounded-full max-w-[210px] truncate">
                    <Lock className="h-3 w-3 text-[#009fe3] shrink-0" />
                    <span className="truncate">https://{study.domain || `${study.id}.co.ke`}</span>
                  </div>

                  <span className="text-xs font-mono text-[#009fe3] bg-slate-900 px-1.5 py-0.5 rounded border border-[#009fe3]/30">
                    LIVE
                  </span>
                </div>

                {/* Domain Snippet Banner */}
                <div className={`h-48 relative overflow-hidden bg-gradient-to-br ${study.accentColor} p-6 flex flex-col justify-between text-white`}>
                  {study.imageUrl && (
                    <CustomImage
                      src={study.imageUrl}
                      alt={`${study.client} (${study.domain || 'thinkersarcade.co.ke'}) - ${study.title} · ${study.industry} live platform in Kenya`}
                      title={`${study.client} - ${study.title}`}
                      width={400}
                      height={200}
                      className="absolute inset-0 w-full h-full object-cover object-top opacity-35 group-hover:scale-105 transition-transform duration-500"
                    />
                  )}

                  <div className="relative z-10 flex items-center justify-between text-xs">
                    <span className="bg-black/50 backdrop-blur-md px-2.5 py-1 rounded-lg font-mono text-xs font-semibold text-white border border-white/10">
                      {study.client}
                    </span>
                    <span className="text-xs bg-white/20 backdrop-blur-md px-2.5 py-0.5 rounded-full text-white">
                      {study.industry}
                    </span>
                  </div>

                  <div className="relative z-10">
                    <div className="text-xl font-bold font-display text-white group-hover:translate-x-1 transition-transform drop-shadow-md">
                      {study.client}
                    </div>
                    <div className="text-xs text-white/90 line-clamp-1 mt-1 font-medium">
                      {study.domainSnippet?.tagline || study.deliverables[0]}
                    </div>
                  </div>
                </div>

                {/* Body Details */}
                <div className="p-6 space-y-4">
                  <div>
                    <div className="text-xs font-mono text-[#b88e4c] font-semibold uppercase tracking-wider mb-1">
                      {study.category}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 font-display mb-2 group-hover:text-[#b88e4c] transition-colors">
                      {study.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {study.summary}
                    </p>
                  </div>

                  {/* Highlights Metrics */}
                  <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
                    {study.metrics.slice(0, 2).map((m) => (
                      <div key={m.label}>
                        <div className="text-base font-extrabold text-slate-900 font-display tabular-nums">
                          {m.value}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Key Snippet Deliverable Tag */}
                  {study.domainSnippet && (
                    <div className="text-xs bg-slate-50 p-2 rounded-lg border border-slate-100 text-slate-700 font-medium">
                      ✓ Snippet: {study.domainSnippet.keyFeatures[0]}
                    </div>
                  )}
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-6 pb-6 pt-0 flex items-center justify-between text-xs border-t border-slate-100 pt-3">
                <span className="text-slate-400 font-mono">Domain snippet verified</span>
                <span className="text-slate-900 group-hover:text-[#b88e4c] font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  View Full Case <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* View More Dropdown Bar for Homepage Density Control */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {remainingCount > 0 && (
            <button
              onClick={() => setShowAllCases(!showAllCases)}
              className="px-6 py-3.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-xs border border-slate-200"
            >
              {showAllCases ? (
                <>
                  <span>Show Fewer on Homepage (Top 6)</span>
                  <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  <span>Expand Snippets on Homepage ({remainingCount} More)</span>
                  <ChevronDown className="h-4 w-4 text-[#b88e4c]" />
                </>
              )}
            </button>
          )}

          {onViewAllWebsites && (
            <button
              onClick={onViewAllWebsites}
              className="px-6 py-3.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-md"
            >
              <span>Explore All 19 Client Websites &amp; Pricing Under Website Development</span>
              <ArrowRight className="h-4 w-4 text-[#c59b4c]" />
            </button>
          )}
        </div>

      </div>
    </section>
  );
};
